// FILE: src/components/PhasePerformance.js
//
// "Performance by Phase" section rendered inside ResultsPage.
// Shows a bar chart (% correct per phase), a pie chart (question distribution),
// and a dynamic insights panel.
//
// ── Props ─────────────────────────────────────────────────────────────────────
//
//   metrics   {PhaseMetric[]}  output of computePhaseMetrics()
//   insights  {string[]}       output of generateInsights()
//   examId    {string}         used to label the dimension (phase / domain)
//
// ── Dependencies ─────────────────────────────────────────────────────────────
//
//   recharts — BarChart, PieChart (loaded lazily so existing tests are unaffected)

import React, { useState } from 'react';
import { PHASE_CONFIG } from '../../utils/phaseAnalytics';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Cell,
  PieChart,
  Pie,
  Legend,
} from 'recharts';

// ── Colour helpers ────────────────────────────────────────────────────────────

function barColor(pct) {
  if (pct >= 70) return '#2e7d32'; // green
  if (pct >= 50) return '#e07b00'; // amber
  return '#c62828';                // red
}

const PIE_PALETTE = [
  '#1565c0', '#2e7d32', '#e07b00', '#6a1b9a', '#00838f', '#c62828',
];

// ── Custom tooltip for bar chart ─────────────────────────────────────────────

function BarTooltip({ active, payload }) {
  if (!active || !payload?.length) return null;
  const d = payload[0].payload;
  return (
    <div style={{
      background: '#fff',
      border: '1px solid #e0e0e0',
      borderRadius: '6px',
      padding: '10px 14px',
      fontSize: '0.85rem',
      boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
      minWidth: '160px',
    }}>
      <div style={{ fontWeight: '700', marginBottom: '6px', color: '#333' }}>
        {d.label}
      </div>
      <div style={{ color: barColor(d.pct), fontWeight: '600', marginBottom: '4px' }}>
        {d.pct}% correct
      </div>
      <div style={{ color: '#666', fontSize: '0.8rem' }}>
        {d.correct} / {d.total} answered correctly
      </div>
      {d.notAnswered > 0 && (
        <div style={{ color: '#999', fontSize: '0.78rem', marginTop: '2px' }}>
          {d.notAnswered} skipped / unanswered
        </div>
      )}
    </div>
  );
}

// ── Custom label for pie chart ────────────────────────────────────────────────

function PieLabel({ cx, cy, midAngle, innerRadius, outerRadius, percent }) {
  if (percent < 0.06) return null; // hide tiny slice labels
  const RADIAN = Math.PI / 180;
  const radius = innerRadius + (outerRadius - innerRadius) * 0.55;
  const x = cx + radius * Math.cos(-midAngle * RADIAN);
  const y = cy + radius * Math.sin(-midAngle * RADIAN);
  return (
    <text
      x={x} y={y}
      fill="#fff"
      textAnchor="middle"
      dominantBaseline="central"
      fontSize={11}
      fontWeight="700"
    >
      {`${Math.round(percent * 100)}%`}
    </text>
  );
}

// ── Styles ────────────────────────────────────────────────────────────────────

const S = {
  section: {
    background: '#fff',
    borderRadius: '12px',
    padding: '24px',
    marginBottom: '20px',
    boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
  },
  heading: {
    fontSize: '1.05rem',
    fontWeight: '700',
    color: '#333',
    margin: '0 0 4px',
  },
  subheading: {
    fontSize: '0.82rem',
    color: '#888',
    margin: '0 0 20px',
  },
  chartsRow: {
    display: 'flex',
    gap: '24px',
    flexWrap: 'wrap',
    alignItems: 'flex-start',
  },
  barWrapper: {
    flex: '2 1 340px',
    minWidth: 0,
  },
  pieWrapper: {
    flex: '1 1 220px',
    minWidth: 0,
  },
  chartTitle: {
    fontSize: '0.78rem',
    fontWeight: '600',
    color: '#666',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    marginBottom: '12px',
  },
  divider: {
    border: 'none',
    borderTop: '1px solid #f0f0f0',
    margin: '20px 0',
  },
  insightsHeading: {
    fontSize: '0.88rem',
    fontWeight: '700',
    color: '#555',
    marginBottom: '10px',
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
  },
  insightsList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  insightRow: (type) => ({
    display: 'flex',
    alignItems: 'flex-start',
    gap: '10px',
    padding: '10px 12px',
    borderRadius: '8px',
    background: type === 'good' ? '#f1f8f1' : type === 'warn' ? '#fff3e0' : '#f5f5f5',
    border: `1px solid ${type === 'good' ? '#c8e6c9' : type === 'warn' ? '#ffe0b2' : '#e8e8e8'}`,
    fontSize: '0.875rem',
    color: '#333',
    lineHeight: '1.5',
  }),
  insightIcon: (type) => ({
    fontSize: '1rem',
    flexShrink: 0,
    marginTop: '1px',
    color: type === 'good' ? '#2e7d32' : type === 'warn' ? '#e07b00' : '#888',
  }),
  phaseTable: {
    width: '100%',
    borderCollapse: 'collapse',
    fontSize: '0.83rem',
    marginTop: '16px',
  },
  th: {
    textAlign: 'left',
    padding: '6px 10px',
    borderBottom: '2px solid #eee',
    color: '#888',
    fontSize: '0.75rem',
    textTransform: 'uppercase',
    letterSpacing: '0.4px',
    fontWeight: '600',
  },
  td: {
    padding: '8px 10px',
    borderBottom: '1px solid #f5f5f5',
    color: '#444',
  },
  pctCell: (pct, hasData) => ({
    fontWeight: '700',
    color: !hasData ? '#bbb' : barColor(pct),
  }),
  toggleButton: {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    color: '#1565c0',
    fontSize: '0.82rem',
    padding: '0',
    marginTop: '16px',
    textDecoration: 'underline',
  },
  legendWrapper: {
    // Span the full charts row (both the bar + pie columns) on its own line.
    flex: '1 1 100%',
    width: '100%',
    boxSizing: 'border-box',
    background: '#fafafa',
    border: '1px solid #eeeeee',
    borderRadius: '8px',
    padding: '12px 16px',
    marginTop: '4px',
  },
  legendGrid: {
    // Flow the key entries across as many columns as fit so it reads wide,
    // not as one tall narrow list.
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
    columnGap: '24px',
    rowGap: '6px',
  },
  legendTitle: {
    fontSize: '0.72rem',
    fontWeight: '700',
    color: '#888',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    marginBottom: '10px',
  },
  legendRow: {
    display: 'flex',
    gap: '8px',
    alignItems: 'baseline',
    marginBottom: '6px',
    fontSize: '0.8rem',
    lineHeight: '1.4',
  },
  legendLabel: {
    fontWeight: '700',
    color: '#444',
    whiteSpace: 'nowrap',
    flexShrink: 0,
  },
  legendKey: {
    color: '#666',
  },
};

// ── Insight type classifier ───────────────────────────────────────────────────

function insightType(text) {
  if (/strong|well-prep|keep it up/i.test(text)) return 'good';
  if (/needs|attention|missed|recommended|before/i.test(text)) return 'warn';
  return 'neutral';
}

function insightIcon(type) {
  if (type === 'good') return '✓';
  if (type === 'warn') return '!';
  return '→';
}

// ── Main component ────────────────────────────────────────────────────────────

export default function PhasePerformance({ metrics, insights, examId }) {
  const [showTable, setShowTable] = useState(false);

  const withData    = metrics.filter((m) => m.hasData);
  const dimensionLabel = examId === 'cpmai' ? 'Phase' : 'Domain';

  if (withData.length === 0) return null;

  // Bar chart data — show all configured phases (empty ones render as 0)
  const barData = metrics
    .filter((m) => m.hasData || metrics.some((x) => x.hasData))
    .map((m) => ({ ...m, pctValue: m.hasData ? m.pct : null }));

  // Pie chart — only phases that actually appeared in this session
  const pieData = withData.map((m, i) => ({
    name: m.label,
    value: m.total,
    fill: PIE_PALETTE[i % PIE_PALETTE.length],
  }));

  return (
    <div data-testid="phase-performance" style={S.section}>
      <h3 style={S.heading}>Performance by {dimensionLabel}</h3>
      <p style={S.subheading}>
        How your score breaks down across each {dimensionLabel.toLowerCase()} of the exam
      </p>

      {/* ── Charts row ───────────────────────────────────────────────────── */}
      <div style={S.chartsRow}>

        {/* Bar chart — % correct per phase */}
        <div style={S.barWrapper}>
          <div style={S.chartTitle}>% Correct by {dimensionLabel}</div>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart
              data={barData}
              margin={{ top: 4, right: 8, left: -18, bottom: 4 }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" vertical={false} />
              <XAxis
                dataKey="label"
                tick={{ fontSize: 11, fill: '#666' }}
                axisLine={false}
                tickLine={false}
              />
              <YAxis
                domain={[0, 100]}
                tick={{ fontSize: 11, fill: '#999' }}
                axisLine={false}
                tickLine={false}
                tickFormatter={(v) => `${v}%`}
              />
              <Tooltip content={<BarTooltip />} cursor={{ fill: 'rgba(0,0,0,0.04)' }} />
              <Bar dataKey="pct" radius={[4, 4, 0, 0]} maxBarSize={52}>
                {barData.map((entry, index) => (
                  <Cell
                    key={`bar-${index}`}
                    fill={entry.hasData ? barColor(entry.pct) : '#e0e0e0'}
                  />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Pie chart — question distribution */}
        <div style={S.pieWrapper}>
          <div style={S.chartTitle}>Question Distribution</div>
          <ResponsiveContainer width="100%" height={220}>
            <PieChart>
              <Pie
                data={pieData}
                dataKey="value"
                nameKey="name"
                cx="50%"
                cy="50%"
                outerRadius={80}
                labelLine={false}
                label={<PieLabel />}
              >
                {pieData.map((entry, index) => (
                  <Cell key={`pie-${index}`} fill={entry.fill} />
                ))}
              </Pie>
              <Legend
                iconType="circle"
                iconSize={8}
                formatter={(value) => (
                  <span style={{ fontSize: '0.78rem', color: '#555' }}>{value}</span>
                )}
              />
            </PieChart>
          </ResponsiveContainer>
        </div>

        {/* Phase legend — maps short labels to full phase names */}
        {PHASE_CONFIG[examId] && (
          <div style={S.legendWrapper}>
            <div style={S.legendTitle}>{dimensionLabel} Key</div>
            <div style={S.legendGrid}>
              {PHASE_CONFIG[examId].map((p) => (
                <div key={p.key} style={S.legendRow}>
                  <span style={S.legendLabel}>{p.label}</span>
                  <span style={S.legendKey}>— {p.key}</span>
                </div>
              ))}
            </div>
          </div>
        )}

      </div>

      {/* ── Insights panel ───────────────────────────────────────────────── */}
      {insights.length > 0 && (
        <>
          <hr style={S.divider} />
          <div style={S.insightsHeading}>
            <span>💡</span> Key Insights
          </div>
          <div style={S.insightsList}>
            {insights.map((text, i) => {
              const type = insightType(text);
              return (
                <div key={i} style={S.insightRow(type)}>
                  <span style={S.insightIcon(type)}>{insightIcon(type)}</span>
                  <span>{text}</span>
                </div>
              );
            })}
          </div>
        </>
      )}

      {/* ── Detail table (collapsed by default) ─────────────────────────── */}
      <button
        style={S.toggleButton}
        onClick={() => setShowTable((v) => !v)}
        data-testid="phase-table-toggle"
      >
        {showTable ? '▲ Hide detail table' : '▼ Show detail table'}
      </button>

      {showTable && (
        <table style={S.phaseTable} data-testid="phase-detail-table">
          <thead>
            <tr>
              <th style={S.th}>{dimensionLabel}</th>
              <th style={{ ...S.th, textAlign: 'right' }}>Questions</th>
              <th style={{ ...S.th, textAlign: 'right' }}>Correct</th>
              <th style={{ ...S.th, textAlign: 'right' }}>Incorrect</th>
              <th style={{ ...S.th, textAlign: 'right' }}>Unanswered</th>
              <th style={{ ...S.th, textAlign: 'right' }}>Score</th>
            </tr>
          </thead>
          <tbody>
            {metrics.map((m) => (
              <tr key={m.key}>
                <td style={S.td}>{m.label}</td>
                <td style={{ ...S.td, textAlign: 'right', color: '#666' }}>
                  {m.hasData ? m.total : '—'}
                </td>
                <td style={{ ...S.td, textAlign: 'right', color: '#2e7d32' }}>
                  {m.hasData ? m.correct : '—'}
                </td>
                <td style={{ ...S.td, textAlign: 'right', color: '#c62828' }}>
                  {m.hasData ? m.incorrect : '—'}
                </td>
                <td style={{ ...S.td, textAlign: 'right', color: '#888' }}>
                  {m.hasData ? m.notAnswered : '—'}
                </td>
                <td style={{ ...S.td, textAlign: 'right', ...S.pctCell(m.pct, m.hasData) }}>
                  {m.hasData ? `${m.pct}%` : 'N/A'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
